import { useEffect, useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';

import { useAdminAuth } from '../auth/AdminAuthContext.jsx';
import { Icon } from '../components/icons.jsx';

/**
 * Admin sign-in page. Mirrors the legacy login.html visual exactly:
 * dark gradient backdrop, white centred card, eyebrow + serif
 * headline + form, secure-note footer.
 *
 * Behaviour:
 *   - If already authed (cached token still valid), Navigate
 *     straight into the console — don't make the user re-enter
 *     credentials for an active session.
 *   - On submit: POST /api/admin/auth/login via the AuthContext,
 *     which stores the token + UserDto.
 *   - Remember the last-typed email locally so a typo retry doesn't
 *     require re-typing.
 *   - Show inline error chip on 401 / 403 / network failure.
 *
 * @author <lukman@zshift.io>
 */
const LAST_EMAIL_KEY = 'noteshq.admin.lastEmail';

export function Login() {
  const { status, login } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState(() => {
    try { return localStorage.getItem(LAST_EMAIL_KEY) || ''; } catch { return ''; }
  });
  const [password, setPassword] = useState('');
  const [showPw, setShowPw]     = useState(false);
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState(null);

  // If a valid session already exists, route straight to the page
  // the user originally tried to reach (or the dashboard if none).
  // The 'restoring' state means /me hasn't landed yet — show nothing
  // for one frame rather than flashing the form.
  if (status === 'restoring') return <div style={{ minHeight: '100vh' }} />;
  if (status === 'authed') {
    const to = location.state?.from?.pathname || '/';
    return <Navigate to={to} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      try { localStorage.setItem(LAST_EMAIL_KEY, email); } catch {}
      await login(email.trim(), password);
      // login() flips status → 'authed'; the redirect above fires
      // on the next render.
      navigate(location.state?.from?.pathname || '/', { replace: true });
    } catch (e) {
      let msg = e.message || 'Sign-in failed.';
      if (e.status === 401) msg = 'Bad email or password.';
      if (e.status === 403) msg = 'This admin account is disabled. Contact a Superadmin.';
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-shell">
      <div className="auth-topline">
        <div className="auth-brand">
          <BrandSvg />
          <div>
            <span className="auth-brand-word">NotesHQ</span>
            <span className="auth-brand-sub">Admin Console</span>
          </div>
        </div>
        <span className="auth-env"><span className="dot"></span> PRODUCTION</span>
      </div>

      <div className="auth-stage">
        <form className="auth-card" onSubmit={onSubmit}>
          <div className="auth-lock">
            <Icon name="lock" size={22} />
          </div>

          <div className="auth-eyebrow">Restricted access</div>
          <h1>Admin <em>sign in</em></h1>
          <p className="auth-lede">
            This console manages live customer data. Authorised staff only —
            all sessions are logged.
          </p>

          <div className="field">
            <label>Work email</label>
            <div className="field-input">
              <Icon name="mail" size={16} />
              <input
                type="email"
                autoComplete="email"
                placeholder="you@noteshq.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                required
              />
            </div>
          </div>

          <div className="field">
            <label>Password</label>
            <div className="field-input">
              <Icon name="lock" size={16} />
              <input
                type={showPw ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="••••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="eye"
                aria-label={showPw ? 'Hide password' : 'Show password'}
                onClick={() => setShowPw((s) => !s)}
              >
                <Icon name="eye" size={16} />
              </button>
            </div>
          </div>

          {err && <div className="auth-err">{err}</div>}

          <button type="submit" className="auth-btn" disabled={busy}>
            {busy ? 'Signing in…' : (
              <>
                Continue to console
                <Icon name="arrowRight" size={15} />
              </>
            )}
          </button>

          <div className="auth-secure">
            <Icon name="shield" size={15} />
            <span>
              Every sign-in is logged. If the email you're using lost console
              access, ask a Superadmin to re-invite.
            </span>
          </div>
        </form>
      </div>

      <div className="auth-footline">
        NotesHQ Admin · v2.4
      </div>
    </div>
  );
}

function BrandSvg() {
  return (
    <svg width="30" height="30" viewBox="0 0 100 100" aria-hidden="true">
      <rect x="14" y="12" width="58" height="58" rx="11" fill="#B7C2F3"/>
      <rect x="18" y="18" width="58" height="58" rx="11" fill="#8092E5"/>
      <rect x="22" y="24" width="58" height="58" rx="11" fill="#2D43E0"/>
      <circle cx="75" cy="25" r="13" fill="#FF6B47"/>
      <text x="75" y="29.5" textAnchor="middle" fontFamily="Geist, system-ui, sans-serif" fontSize="11" fontWeight="700" fill="white">HQ</text>
    </svg>
  );
}
